import { useState, useEffect, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Separator } from '@/components/ui/separator';
import axios from 'axios';
import {
  Menu, X, ArrowRight, ChevronDown, Ruler, Pencil, Home as HomeIcon, Building2,
  Layers, PaintBucket
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// New colored logo
const LOGO_URL = "https://customer-assets.emergentagent.com/job_blueprint-studio-34/artifacts/fe0mwn3b_image.png";

// Lazy image component — only loads when near viewport, shows placeholder until loaded
function LazyImg({ src, alt, className, style, onMouseEnter, onMouseLeave }) {
  const [loaded, setLoaded] = useState(false);
  const [inView, setInView] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setInView(true); obs.disconnect(); } }, { rootMargin: '300px' });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return (
    <div ref={ref} className="relative" style={style}>
      {!loaded && <div className="absolute inset-0 bg-[#E5E5E5] animate-pulse" />}
      {inView && <img src={src} alt={alt || ''} loading="lazy" decoding="async"
        className={`${className || ''} transition-opacity duration-500 ${loaded ? 'opacity-100' : 'opacity-0'}`}
        style={style} onLoad={() => setLoaded(true)} onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave} />}
    </div>
  );
}

// Scenic images
const SCENIC = {
  hero: "https://customer-assets.emergentagent.com/job_blueprint-studio-34/artifacts/hht0z00i_ChatGPT%20Image%20Mar%2025%2C%202026%2C%2011_07_36%20PM.png",
  nightAerial: "https://customer-assets.emergentagent.com/job_blueprint-studio-34/artifacts/yf3dfc7b_ChatGPT%20Image%20Mar%2025%2C%202026%2C%2011_20_24%20PM.png",
  dayAerial: "https://customer-assets.emergentagent.com/job_blueprint-studio-34/artifacts/j31k3tlk_82A%20Neugrabener%20Bahnhofstra%C3%9Fe%2C%20Hamburg%20top.png",
  garden: "https://customer-assets.emergentagent.com/job_blueprint-studio-34/artifacts/kjzqk0l6_82A%20Neugrabener%20Bahnhofstra%C3%9Fe%2C%20Hamburg%20top2.png",
  rainyEvening: "https://customer-assets.emergentagent.com/job_blueprint-studio-34/artifacts/vhqjpgg2_1.jpg",
};

// Wireframe images (matching 3D wireframes for each showcase)
const WIREFRAMES = {
  eveningAmbience: "https://customer-assets.emergentagent.com/job_blueprint-studio-34/artifacts/3jxqbwnw_Evening%20Ambience.png.png",
  sunlitPerspective: "https://customer-assets.emergentagent.com/job_blueprint-studio-34/artifacts/ye8tadtk_Sunlit%20perspective.png.png",
  gardenHarmony: "https://customer-assets.emergentagent.com/job_blueprint-studio-34/artifacts/nuqn7ajv_Garden%20Harmony.png.png",
  moodyElegance: "https://customer-assets.emergentagent.com/job_blueprint-studio-34/artifacts/j7haxgnb_MOODY%20ELEGANCE.png",
};

// New project images (replace old sketches)
const PROJECT_IMAGES = {
  building: "https://customer-assets.emergentagent.com/job_blueprint-studio-34/artifacts/hf0ktp8x_S.A.I.%20DESIGN%20GOUP.jpg",
  interiorLiving: "https://customer-assets.emergentagent.com/job_blueprint-studio-34/artifacts/ml1g1dgf_506701153_3105901392910795_343496597113671929_n.jpg",
  interiorStaircase: "https://customer-assets.emergentagent.com/job_blueprint-studio-34/artifacts/h6yyiqz9_504835053_3105901402910794_5202219825644815771_n.jpg",
};

// Client brand logos — using the uploaded "Our Precious Clients" image as reference
// Logo image URL for the full clients collage
const CLIENTS_IMAGE = "https://customer-assets.emergentagent.com/job_blueprint-studio-34/artifacts/51m9fss0_our%20clients.jpg";

// Individual client entries with name and brand color
const CLIENT_LOGOS = [
  { name: "WhiteHR", color: "#00B2FF", bg: "#000" },
  { name: "BYJU'S", color: "#fff", bg: "#7B2D8E" },
  { name: "Blinkit", color: "#000", bg: "#F5D100" },
  { name: "Krisumi", color: "#C8A96E", bg: "#000" },
  { name: "The Fit Food", color: "#E67E22", bg: "#FFF5E6" },
  { name: "Nagarro", color: "#00A88E", bg: "#fff" },
  { name: "Zepto", color: "#6C3EC1", bg: "#fff" },
  { name: "Flipkart", color: "#2874F0", bg: "#F0C400" },
  { name: "RLDA", color: "#1B5E20", bg: "#fff" },
  { name: "Amazon", color: "#FF9900", bg: "#232F3E" },
  { name: "TCRC", color: "#8B7355", bg: "#fff" },
  { name: "Taj Hotels", color: "#8B6914", bg: "#000" },
  { name: "Mumbai Metro", color: "#E91E63", bg: "#fff" },
  { name: "Raftaar", color: "#FF1744", bg: "#1A1A1A" },
  { name: "Hippo Stores", color: "#FF6D00", bg: "#fff" },
  { name: "Airtel", color: "#fff", bg: "#E60000" },
  { name: "Zomato", color: "#fff", bg: "#E23744" },
  { name: "Incuspaze", color: "#1A237E", bg: "#fff" },
  { name: "Tornado Cafe", color: "#D32F2F", bg: "#fff" },
  { name: "MAXVAL", color: "#333", bg: "#fff" },
  { name: "Breath Lounge", color: "#5D4037", bg: "#fff" },
  { name: "MI Rustle Court", color: "#6D4C41", bg: "#fff" },
];

const SOCIAL = {
  instagram: "https://instagram.com/solution_added_ideas",
  facebook: "https://www.facebook.com/solutionaddedideas/",
  youtube: "https://www.youtube.com/@solutionaddedideas"
};

const SERVICES = [
  { icon: Ruler, title: "Architectural Design", desc: "Complete architectural planning from concept to construction-ready blueprints." },
  { icon: PaintBucket, title: "Interior Design", desc: "Transform spaces with curated aesthetics, materials, and furnishing solutions." },
  { icon: Building2, title: "3D Visualization", desc: "Photorealistic 3D renders and walkthroughs of your dream spaces." },
  { icon: Layers, title: "Project Management", desc: "End-to-end supervision ensuring timely delivery and quality execution." },
  { icon: Pencil, title: "Renovation & Remodeling", desc: "Breathe new life into existing structures with smart redesign solutions." },
  { icon: HomeIcon, title: "Turnkey Solutions", desc: "From blueprint to handover — we handle every step of your project." }
];

// ====== ANIMATED SOCIAL ICONS ======
function InstagramIcon({ className }) {
  return (
    <div className={`social-icon-flip ${className || ''}`}>
      <div className="social-icon-inner">
        <div className="social-icon-front">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="url(#igGrad)" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
            <defs><linearGradient id="igGrad" x1="0%" y1="100%" x2="100%" y2="0%"><stop offset="0%" stopColor="#feda75"/><stop offset="25%" stopColor="#fa7e1e"/><stop offset="50%" stopColor="#d62976"/><stop offset="75%" stopColor="#962fbf"/><stop offset="100%" stopColor="#4f5bd5"/></linearGradient></defs>
            <rect x="2" y="2" width="20" height="20" rx="5"/><circle cx="12" cy="12" r="5"/><circle cx="17.5" cy="6.5" r="1.2" fill="url(#igGrad)" stroke="none"/>
          </svg>
        </div>
        <div className="social-icon-back">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#2A2A2A" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
            <rect x="2" y="2" width="20" height="20" rx="5"/><circle cx="12" cy="12" r="5"/><circle cx="17.5" cy="6.5" r="1.2" fill="#2A2A2A" stroke="none"/>
          </svg>
        </div>
      </div>
    </div>
  );
}
function FacebookIcon({ className }) {
  return (
    <div className={`social-icon-flip ${className || ''}`} style={{ animationDelay: '1s' }}>
      <div className="social-icon-inner">
        <div className="social-icon-front">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#1877F2" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"><path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"/></svg>
        </div>
        <div className="social-icon-back">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#2A2A2A" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"><path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"/></svg>
        </div>
      </div>
    </div>
  );
}
function YoutubeIcon({ className }) {
  return (
    <div className={`social-icon-flip ${className || ''}`} style={{ animationDelay: '2s' }}>
      <div className="social-icon-inner">
        <div className="social-icon-front">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#FF0000" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
            <path d="M2.5 17a24.12 24.12 0 0 1 0-10 2 2 0 0 1 1.4-1.4 49.56 49.56 0 0 1 16.2 0A2 2 0 0 1 21.5 7a24.12 24.12 0 0 1 0 10 2 2 0 0 1-1.4 1.4 49.55 49.55 0 0 1-16.2 0A2 2 0 0 1 2.5 17"/><path d="m10 15 5-3-5-3z"/>
          </svg>
        </div>
        <div className="social-icon-back">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#2A2A2A" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
            <path d="M2.5 17a24.12 24.12 0 0 1 0-10 2 2 0 0 1 1.4-1.4 49.56 49.56 0 0 1 16.2 0A2 2 0 0 1 21.5 7a24.12 24.12 0 0 1 0 10 2 2 0 0 1-1.4 1.4 49.55 49.55 0 0 1-16.2 0A2 2 0 0 1 2.5 17"/><path d="m10 15 5-3-5-3z"/>
          </svg>
        </div>
      </div>
    </div>
  );
}

// ====== ANIMATED CONTACT ICONS (colorful ↔ B&W flip) ======
function PhoneFlipIcon() {
  return (
    <div className="social-icon-flip" style={{ animationDelay: '0.5s' }}>
      <div className="social-icon-inner">
        <div className="social-icon-front">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#22C55E" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
            <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
          </svg>
        </div>
        <div className="social-icon-back">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#2A2A2A" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
            <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
          </svg>
        </div>
      </div>
    </div>
  );
}
function MailFlipIcon() {
  return (
    <div className="social-icon-flip" style={{ animationDelay: '1.5s' }}>
      <div className="social-icon-inner">
        <div className="social-icon-front">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#EF4444" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
            <rect width="20" height="16" x="2" y="4" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/>
          </svg>
        </div>
        <div className="social-icon-back">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#2A2A2A" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
            <rect width="20" height="16" x="2" y="4" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/>
          </svg>
        </div>
      </div>
    </div>
  );
}
function MapPinFlipIcon() {
  return (
    <div className="social-icon-flip" style={{ animationDelay: '2.5s' }}>
      <div className="social-icon-inner">
        <div className="social-icon-front">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#3B82F6" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
            <path d="M20 10c0 4.993-5.539 10.193-7.399 11.799a1 1 0 0 1-1.202 0C9.539 20.193 4 14.993 4 10a8 8 0 0 1 16 0"/><circle cx="12" cy="10" r="3"/>
          </svg>
        </div>
        <div className="social-icon-back">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#2A2A2A" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
            <path d="M20 10c0 4.993-5.539 10.193-7.399 11.799a1 1 0 0 1-1.202 0C9.539 20.193 4 14.993 4 10a8 8 0 0 1 16 0"/><circle cx="12" cy="10" r="3"/>
          </svg>
        </div>
      </div>
    </div>
  );
}

// ====== FLAG ICONS (small inline) ======
function IndiaFlag() {
  return (
    <svg width="20" height="14" viewBox="0 0 20 14" className="inline-block ml-1.5 align-middle rounded-sm shadow-sm">
      <rect width="20" height="4.67" fill="#FF9933"/>
      <rect y="4.67" width="20" height="4.67" fill="#FFFFFF"/>
      <rect y="9.33" width="20" height="4.67" fill="#138808"/>
      <circle cx="10" cy="7" r="1.8" fill="none" stroke="#000080" strokeWidth="0.4"/>
    </svg>
  );
}
function GermanyFlag() {
  return (
    <svg width="20" height="14" viewBox="0 0 20 14" className="inline-block ml-1.5 align-middle rounded-sm shadow-sm">
      <rect width="20" height="4.67" fill="#000000"/>
      <rect y="4.67" width="20" height="4.67" fill="#DD0000"/>
      <rect y="9.33" width="20" height="4.67" fill="#FFCC00"/>
    </svg>
  );
}

// ====== SCROLL REVEAL HERO (B&W sketch → Color) ======
function ScrollRevealHero() {
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const vh = typeof window !== 'undefined' ? window.innerHeight : 800;
  const progress = Math.min(scrollY / vh, 2);
  const splitProgress = Math.max(0, Math.min((progress - 0.5) / 1, 1));
  const splitAmount = splitProgress * 55;
  const heroScale = 1 + splitProgress * 0.15;
  const overlayOpacity = Math.max(0, 0.25 - splitProgress * 0.25);
  const scrollIndicatorOpacity = Math.max(0, 1 - progress * 3);
  const titleY = splitProgress * -60;
  const textOpacity = Math.min(progress / 0.3, 1);
  // B&W to color: starts fully grayscale but BRIGHT, transitions to full color as you scroll
  const grayscaleAmount = Math.max(0, 100 - (progress * 120));
  const brightnessAmount = 1.4 - (Math.min(progress, 0.8) * 0.5); // starts at 1.4 (bright), goes to ~1.0
  const imgFilter = `grayscale(${grayscaleAmount}%) brightness(${brightnessAmount})`;

  return (
    <div data-testid="scroll-reveal-hero" style={{ height: '250vh' }}>
      <div className="fixed inset-0 z-30" style={{
        opacity: splitProgress >= 1 ? 0 : 1,
        pointerEvents: splitProgress >= 1 ? 'none' : 'auto',
        transition: splitProgress >= 1 ? 'opacity 0.5s' : 'none'
      }}>
        {/* Left curtain */}
        <div className="absolute inset-0 overflow-hidden" style={{
          clipPath: `inset(0 ${50 + splitAmount}% 0 0)`,
          transform: `translateX(-${splitAmount}%)`,
        }}>
          <img src={SCENIC.hero} alt="S.A.I. Design Group" fetchpriority="high"
            className="w-full h-full object-cover"
            style={{ transform: `scale(${heroScale})`, filter: imgFilter, transition: 'filter 0.1s' }} />
        </div>
        {/* Right curtain */}
        <div className="absolute inset-0 overflow-hidden" style={{
          clipPath: `inset(0 0 0 ${50 + splitAmount}%)`,
          transform: `translateX(${splitAmount}%)`,
        }}>
          <img src={SCENIC.hero} alt="S.A.I. Design Group" fetchpriority="high"
            className="w-full h-full object-cover"
            style={{ transform: `scale(${heroScale})`, filter: imgFilter, transition: 'filter 0.1s' }} />
        </div>
        {/* Dark overlay */}
        <div className="absolute inset-0 bg-black" style={{ opacity: overlayOpacity }} />
        {/* Center line */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-px h-full bg-white/20"
          style={{ opacity: Math.max(0, 1 - splitProgress * 3) }} />
        {/* Center branding */}
        <div className="absolute inset-0 flex flex-col items-center justify-center z-10 px-6"
          style={{ opacity: textOpacity, transform: `translateY(${titleY}px)` }}>
          <img src={LOGO_URL} alt="S.A.I." className="h-20 md:h-28 mb-4 drop-shadow-2xl" />
          <p className="text-lg md:text-2xl text-white/80 tracking-[0.3em] uppercase mt-2 font-light drop-shadow-lg">
            Solution &middot; Added &middot; Ideas
          </p>
        </div>
        {/* Scroll indicator */}
        <div className="absolute bottom-12 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2"
          style={{ opacity: scrollIndicatorOpacity }}>
          <p className="text-white/60 text-xs tracking-[0.3em] uppercase">Scroll</p>
          <div className="w-px h-10 bg-gradient-to-b from-white/60 to-transparent animate-pulse" />
        </div>
      </div>
    </div>
  );
}

// ====== PARALLAX SHOWCASE (Wireframe → 3D Color on HOVER) ======
function ParallaxShowcase() {
  const items = [
    { wireframe: WIREFRAMES.eveningAmbience, color: SCENIC.nightAerial, title: "Evening Ambiance", subtitle: "Aerial Night View" },
    { wireframe: WIREFRAMES.sunlitPerspective, color: SCENIC.dayAerial, title: "Sunlit Perspective", subtitle: "Daylight Aerial" },
    { wireframe: WIREFRAMES.gardenHarmony, color: SCENIC.garden, title: "Garden Harmony", subtitle: "Landscape Design" },
    { wireframe: WIREFRAMES.moodyElegance, color: SCENIC.rainyEvening, title: "Glass House at Hamburg", subtitle: "Architecture" },
  ];

  return (
    <section data-testid="parallax-showcase">
      {items.map((item, i) => (
        <div key={i} className="group relative overflow-hidden h-[70vh] cursor-pointer">
          {/* Wireframe layer (default visible, fades out on hover) */}
          <div className="absolute inset-0 transition-opacity duration-500 ease-out group-hover:opacity-0">
            <LazyImg src={item.wireframe} alt={`${item.title} wireframe`}
              className="w-full h-full object-cover"
              style={{ filter: 'brightness(1.4) contrast(1.1)' }} />
          </div>
          {/* 3D Color layer (hidden by default, fades in on hover) */}
          <div className="absolute inset-0 opacity-0 transition-opacity duration-500 ease-out group-hover:opacity-100">
            <LazyImg src={item.color} alt={item.title}
              className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
              style={{ filter: 'brightness(1.15) saturate(1.25) contrast(1.05)' }} />
          </div>
          {/* Gradient overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent z-10 pointer-events-none" />
          {/* Text overlay */}
          <div className="absolute bottom-0 left-0 right-0 p-8 md:p-16 z-20">
            <p className="text-xs tracking-[0.3em] uppercase text-white/60 mb-2">{item.subtitle}</p>
            <h3 className="text-3xl md:text-5xl font-bold text-white tracking-tight drop-shadow-lg">{item.title}</h3>
            <p className="text-xs text-white/40 mt-3 tracking-wider uppercase opacity-0 group-hover:opacity-100 transition-opacity duration-700">
              Hover to reveal the design
            </p>
          </div>
          <div className="absolute bottom-0 left-0 right-0 h-px bg-white/10 z-20" />
        </div>
      ))}
    </section>
  );
}

// ====== NAVBAR ======
function Navbar() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { user, login } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > window.innerHeight * 1.8);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <nav data-testid="navbar" className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${scrolled ? 'bg-[#FDFCFB] border-b border-[#E5E5E5] shadow-sm' : 'bg-transparent'}`}>
      <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between">
        <a href="/" className="flex items-center gap-3" data-testid="logo-link">
          <img src={LOGO_URL} alt="S.A.I. Design Group" className="h-11 w-auto" />
          {scrolled && (
            <div className="hidden sm:block">
              <span className="text-base font-bold tracking-tight text-[#121212]">S.A.I.</span>
              <span className="text-[10px] tracking-[0.2em] uppercase block text-[#5C5C5C] -mt-0.5">Design Group</span>
            </div>
          )}
        </a>
        <div className="hidden lg:flex items-center gap-8">
          {["About", "Projects", "Services", "Contact"].map(item => (
            <a key={item} href={`#${item.toLowerCase()}`} data-testid={`nav-${item.toLowerCase()}`}
              className={`text-sm font-medium transition-colors tracking-wide uppercase ${scrolled ? 'text-[#2A2A2A] hover:text-[#121212]' : 'text-white/80 hover:text-white'}`}>
              {item}
            </a>
          ))}
        </div>
        <div className="hidden lg:flex items-center gap-4">
          <div className="flex items-center gap-3">
            <a href={SOCIAL.instagram} target="_blank" rel="noopener noreferrer" data-testid="social-instagram"><InstagramIcon /></a>
            <a href={SOCIAL.facebook} target="_blank" rel="noopener noreferrer" data-testid="social-facebook"><FacebookIcon /></a>
            <a href={SOCIAL.youtube} target="_blank" rel="noopener noreferrer" data-testid="social-youtube"><YoutubeIcon /></a>
          </div>
          <Separator orientation="vertical" className={`h-6 ${scrolled ? '' : 'bg-white/20'}`} />
          {user ? (
            <Button data-testid="dashboard-btn" onClick={() => navigate('/dashboard')}
              className={`rounded-none px-6 text-xs tracking-[0.15em] uppercase h-10 ${scrolled ? 'bg-[#121212] text-white hover:bg-[#2A2A2A]' : 'bg-white text-[#121212] hover:bg-white/90'}`}>
              Dashboard <ArrowRight size={14} className="ml-2" />
            </Button>
          ) : (
            <Button data-testid="login-btn" onClick={() => navigate('/login')}
              className={`rounded-none px-6 text-xs tracking-[0.15em] uppercase h-10 ${scrolled ? 'bg-[#121212] text-white hover:bg-[#2A2A2A]' : 'bg-white text-[#121212] hover:bg-white/90'}`}>
              Sign In <ArrowRight size={14} className="ml-2" />
            </Button>
          )}
        </div>
        <button className="lg:hidden p-2" onClick={() => setMenuOpen(!menuOpen)} data-testid="mobile-menu-btn">
          {menuOpen ? <X size={24} className={scrolled ? '' : 'text-white'} /> : <Menu size={24} className={scrolled ? '' : 'text-white'} />}
        </button>
      </div>
      {menuOpen && (
        <div className={`lg:hidden px-6 py-6 space-y-4 ${scrolled ? 'bg-[#FDFCFB] border-t border-[#E5E5E5]' : 'bg-black/80 backdrop-blur-md'}`}>
          {["About", "Projects", "Services", "Contact"].map(item => (
            <a key={item} href={`#${item.toLowerCase()}`} onClick={() => setMenuOpen(false)}
              className={`block text-sm font-medium uppercase tracking-wide py-2 ${scrolled ? 'text-[#2A2A2A]' : 'text-white'}`}>{item}</a>
          ))}
          <Separator className={scrolled ? '' : 'bg-white/20'} />
          <Button onClick={() => { user ? navigate('/dashboard') : navigate('/login'); setMenuOpen(false); }}
            className="w-full bg-white text-[#121212] rounded-none h-10 text-xs tracking-[0.15em] uppercase">{user ? 'Dashboard' : 'Sign In'}</Button>
        </div>
      )}
    </nav>
  );
}

// ====== CONTENT INTRO ======
function ContentIntro() {
  const { login, user } = useAuth();
  const navigate = useNavigate();

  return (
    <section className="relative py-24 px-6 bg-[#FDFCFB]">
      <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-16 items-center">
        <div className="animate-fade-in-up">
          <p className="text-xs tracking-[0.3em] uppercase text-[#5C5C5C] mb-6 font-medium">Architecture &middot; Interior &middot; Design</p>
          <h2 className="text-4xl md:text-5xl tracking-tighter leading-none font-bold text-[#121212] mb-6">
            Crafting Spaces<br /><span className="text-[#5C5C5C]">That Inspire</span>
          </h2>
          <p className="text-base leading-relaxed text-[#5C5C5C] max-w-lg mb-8">
            S.A.I. Design Group brings architectural visions to life. From conceptual sketches
            to fully realized spaces, we combine precision engineering with creative artistry.
          </p>
          <div className="flex flex-wrap gap-4">
            <Button data-testid="hero-cta" onClick={() => user ? navigate('/dashboard') : navigate('/login')}
              className="bg-[#121212] text-white hover:bg-[#2A2A2A] rounded-none px-8 h-12 text-xs tracking-[0.15em] uppercase hover:-translate-y-1 transition-all duration-300 hover:shadow-lg">
              {user ? 'Go to Dashboard' : 'Get Started'} <ArrowRight size={14} className="ml-2" />
            </Button>
            <a href="#projects">
              <Button variant="outline" data-testid="hero-projects-btn"
                className="border-[#121212] text-[#121212] rounded-none px-8 h-12 text-xs tracking-[0.15em] uppercase hover:-translate-y-1 transition-all duration-300">View Projects</Button>
            </a>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-1 animate-fade-in-up animate-delay-300">
          <div className="group border border-[#E5E5E5] bg-white overflow-hidden">
            <LazyImg src={PROJECT_IMAGES.building} alt="Modern Building" className="w-full h-48 object-cover grayscale transition-all duration-[800ms] ease-out group-hover:grayscale-0 group-hover:scale-105" />
          </div>
          <div className="group border border-[#E5E5E5] bg-white overflow-hidden">
            <LazyImg src={PROJECT_IMAGES.interiorLiving} alt="Interior Design" className="w-full h-48 object-cover grayscale transition-all duration-[800ms] ease-out group-hover:grayscale-0 group-hover:scale-105" />
          </div>
          <div className="col-span-2 group border border-[#E5E5E5] bg-white overflow-hidden">
            <LazyImg src={PROJECT_IMAGES.interiorStaircase} alt="Staircase Design" className="w-full h-48 object-cover grayscale transition-all duration-[800ms] ease-out group-hover:grayscale-0 group-hover:scale-105" />
          </div>
        </div>
      </div>
    </section>
  );
}

function AboutSection() {
  return (
    <section id="about" data-testid="about-section" className="py-20 border-t border-[#E5E5E5] bg-[#FDFCFB]">
      <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-16 items-center">
        <div>
          <p className="text-xs tracking-[0.3em] uppercase text-[#5C5C5C] mb-4 font-bold">About Us</p>
          <h2 className="text-3xl md:text-4xl tracking-tight leading-tight font-bold text-[#121212] mb-6">Where Solutions Meet<br />Creative Ideas</h2>
          <p className="text-base leading-relaxed text-[#5C5C5C] mb-6">
            <strong>S.A.I. Design Group</strong> — where <em>S</em> stands for Solution, <em>A</em> stands for Added,
            and <em>I</em> stands for Ideas. We are a full-service architecture and interior design firm
            committed to delivering innovative, functional, and aesthetically compelling spaces.
          </p>
          <p className="text-base leading-relaxed text-[#5C5C5C] mb-8">
            Our team of experienced architects, interior designers, and project managers work collaboratively
            with clients, vendors, and contractors to ensure every project is executed with precision.
          </p>
          <div className="grid grid-cols-3 gap-6">
            {[{ num: "150+", label: "Projects Completed" }, { num: "12+", label: "Years Experience" }, { num: "130+", label: "Happy Clients" }].map(s => (
              <div key={s.label} className="border border-[#E5E5E5] p-4">
                <p className="text-2xl font-bold text-[#121212]">{s.num}</p>
                <p className="text-xs text-[#5C5C5C] uppercase tracking-wider mt-1">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
        <div className="border border-[#E5E5E5] p-2 bg-white">
          <LazyImg src={PROJECT_IMAGES.building} alt="Building" className="w-full h-[400px] object-cover sketch-to-color" style={{ filter: 'brightness(1.15) saturate(1.2)' }} />
        </div>
      </div>
    </section>
  );
}

function ProjectsSection() {
  const [galleryItems, setGalleryItems] = useState([]);

  useEffect(() => {
    axios.get(`${API}/gallery`).then(r => setGalleryItems(r.data)).catch(() => {});
  }, []);

  const DEFAULT_GALLERY = [
    { url: SCENIC.hero, title: "Night Elegance", cat: "Residential" },
    { url: PROJECT_IMAGES.building, title: "Modern Facade", cat: "Architecture" },
    { url: PROJECT_IMAGES.interiorLiving, title: "Luxury Living", cat: "Interior" },
    { url: SCENIC.garden, title: "Garden Retreat", cat: "Landscape" },
    { url: PROJECT_IMAGES.interiorStaircase, title: "Art Wall Staircase", cat: "Interior" },
    { url: SCENIC.rainyEvening, title: "Glass House at Hamburg", cat: "Architecture" },
  ];

  // Merge: uploaded gallery images first, then defaults
  const uploadedItems = galleryItems.map(g => ({
    url: `${API}/gallery/${g.gallery_id}/image`,
    title: g.title || 'Project',
    cat: g.description || 'Design'
  }));
  const GALLERY = [...uploadedItems, ...DEFAULT_GALLERY];

  return (
    <section id="projects" data-testid="projects-section" className="py-20 border-t border-[#E5E5E5] bg-[#FDFCFB]">
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex items-end justify-between mb-12">
          <div>
            <p className="text-xs tracking-[0.3em] uppercase text-[#5C5C5C] mb-4 font-bold">Portfolio</p>
            <h2 className="text-3xl md:text-4xl tracking-tight leading-tight font-bold text-[#121212]">Featured Projects</h2>
          </div>
          <p className="hidden md:block text-sm text-[#5C5C5C] max-w-xs text-right">Hover to reveal</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-1">
          {GALLERY.map((img, i) => (
            <div key={i} className="group border border-[#E5E5E5] bg-white overflow-hidden">
              <div className="relative overflow-hidden">
                <LazyImg src={img.url} alt={img.title} className="w-full h-64 object-cover grayscale transition-all duration-[800ms] ease-out group-hover:grayscale-0 group-hover:scale-105" style={{ filter: undefined }} 
                  onMouseEnter={e => { if (e.target.tagName === 'IMG') e.target.style.filter = 'brightness(1.15) saturate(1.2)'; }}
                  onMouseLeave={e => { if (e.target.tagName === 'IMG') e.target.style.filter = 'grayscale(100%) brightness(1.3)'; }} />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-all duration-500" />
              </div>
              <div className="p-4 border-t border-[#E5E5E5]">
                <p className="text-xs tracking-[0.2em] uppercase text-[#5C5C5C] mb-1">{img.cat}</p>
                <h3 className="text-sm font-bold text-[#121212]">{img.title}</h3>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function ServicesSection() {
  return (
    <section id="services" data-testid="services-section" className="py-20 border-t border-[#E5E5E5] bg-[#FDFCFB]">
      <div className="max-w-7xl mx-auto px-6">
        <div className="mb-12">
          <p className="text-xs tracking-[0.3em] uppercase text-[#5C5C5C] mb-4 font-bold">What We Do</p>
          <h2 className="text-3xl md:text-4xl tracking-tight leading-tight font-bold text-[#121212]">Our Services</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-1">
          {SERVICES.map(svc => (
            <div key={svc.title} className="group border border-[#E5E5E5] p-8 bg-white hover:bg-[#121212] transition-all duration-500">
              <svc.icon size={28} strokeWidth={1.25} className="text-[#121212] group-hover:text-white transition-colors mb-6" />
              <h3 className="text-lg font-bold text-[#121212] group-hover:text-white transition-colors mb-3">{svc.title}</h3>
              <p className="text-sm text-[#5C5C5C] group-hover:text-white/70 transition-colors leading-relaxed">{svc.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ====== CLIENT LOGOS MARQUEE (Colorful brand cards) ======
function ClientLogosSection() {
  const allLogos = [...CLIENT_LOGOS, ...CLIENT_LOGOS];

  return (
    <section data-testid="client-logos-section" className="py-16 border-t border-[#E5E5E5] bg-[#FDFCFB] overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 mb-10">
        <p className="text-xs tracking-[0.3em] uppercase text-[#5C5C5C] mb-4 font-bold">Trusted By</p>
        <h2 className="text-3xl md:text-4xl tracking-tight leading-tight font-bold text-[#121212]">Our Prestigious Clients</h2>
      </div>
      <div className="relative">
        <div className="absolute left-0 top-0 bottom-0 w-20 bg-gradient-to-r from-[#FDFCFB] to-transparent z-10" />
        <div className="absolute right-0 top-0 bottom-0 w-20 bg-gradient-to-l from-[#FDFCFB] to-transparent z-10" />
        <div className="flex items-center gap-5 animate-marquee">
          {allLogos.map((c, i) => (
            <div key={i}
              className="flex-shrink-0 px-5 py-2.5 rounded-sm border border-[#E5E5E5] flex items-center gap-2 hover:scale-105 transition-transform duration-300 cursor-pointer shadow-sm hover:shadow-md"
              style={{ backgroundColor: c.bg }}>
              <span className="text-sm font-bold tracking-wide whitespace-nowrap" style={{ color: c.color }}>
                {c.name}
              </span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ====== CAREERS TEASER ======
function CareersTeaser() {
  return (
    <section data-testid="careers-teaser" className="py-16 border-t border-[#E5E5E5] bg-[#121212]">
      <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-8">
        <div>
          <p className="text-xs tracking-[0.3em] uppercase text-white/50 mb-3 font-bold">Join Our Team</p>
          <h2 className="text-2xl md:text-3xl tracking-tight font-bold text-white mb-2">We're Hiring</h2>
          <p className="text-sm text-white/50 max-w-md">Looking for talented architects, interior designers, civil engineers & contractors to join S.A.I. Design Group.</p>
        </div>
        <div className="flex gap-3">
          <a href="/careers">
            <Button className="bg-white text-[#121212] hover:bg-white/90 rounded-none px-8 h-11 text-xs tracking-[0.15em] uppercase" data-testid="careers-link">
              View Openings
            </Button>
          </a>
          <a href="/team">
            <Button variant="outline" className="border-white/30 text-white hover:bg-white/10 rounded-none px-8 h-11 text-xs tracking-[0.15em] uppercase" data-testid="team-link">
              Meet the Team
            </Button>
          </a>
        </div>
      </div>
    </section>
  );
}

function ContactSection() {
  const [form, setForm] = useState({ name: '', email: '', phone: '', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try { await axios.post(`${API}/contact`, form); setSubmitted(true); setForm({ name: '', email: '', phone: '', message: '' }); }
    catch {} finally { setSubmitting(false); }
  };

  return (
    <section id="contact" data-testid="contact-section" className="py-20 border-t border-[#E5E5E5] bg-[#FDFCFB]">
      <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-16">
        <div>
          <p className="text-xs tracking-[0.3em] uppercase text-[#5C5C5C] mb-4 font-bold">Get In Touch</p>
          <h2 className="text-3xl md:text-4xl tracking-tight leading-tight font-bold text-[#121212] mb-6">Let's Build<br />Something Together</h2>
          <p className="text-base leading-relaxed text-[#5C5C5C] mb-8">Ready to start your project? Reach out and let's discuss how we can bring your vision to life.</p>
          <div className="space-y-5">
            {/* Phone */}
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 border border-[#E5E5E5] flex items-center justify-center">
                <PhoneFlipIcon />
              </div>
              <div className="flex flex-col">
                <span className="text-sm text-[#2A2A2A]">+91-9990288864</span>
                <span className="text-sm text-[#2A2A2A]">+49 176 28699587</span>
              </div>
            </div>
            {/* Email */}
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 border border-[#E5E5E5] flex items-center justify-center">
                <MailFlipIcon />
              </div>
              <span className="text-sm text-[#2A2A2A]">saidesigngroup2013@gmail.com</span>
            </div>
            {/* India Address */}
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 border border-[#E5E5E5] flex items-center justify-center flex-shrink-0">
                <MapPinFlipIcon />
              </div>
              <div>
                <span className="text-sm text-[#2A2A2A]">
                  Office 404, SVH 83 Metro Street, Sector 83, Gurugram, Haryana 122004, India
                </span>
                <IndiaFlag />
              </div>
            </div>
            {/* Germany Address */}
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 border border-[#E5E5E5] flex items-center justify-center flex-shrink-0">
                <MapPinFlipIcon />
              </div>
              <div>
                <span className="text-sm text-[#2A2A2A]">
                  Neugraben, Hamburg, 21147, Germany
                </span>
                <GermanyFlag />
              </div>
            </div>
          </div>
        </div>
        <div className="border border-[#E5E5E5] p-8 bg-white">
          {submitted ? (
            <div data-testid="contact-success" className="text-center py-12">
              <p className="text-lg font-bold text-[#121212] mb-2">Message Sent</p>
              <p className="text-sm text-[#5C5C5C]">Thank you. We'll get back to you shortly.</p>
              <Button onClick={() => setSubmitted(false)} className="mt-6 bg-[#121212] text-white rounded-none">Send Another</Button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label className="text-xs tracking-[0.2em] uppercase font-bold text-[#5C5C5C] mb-2 block">Name</label>
                <Input data-testid="contact-name" placeholder="Your full name" value={form.name}
                  onChange={e => setForm({...form, name: e.target.value})} required className="rounded-none border-[#E5E5E5] focus:ring-2 focus:ring-[#121212] h-11" />
              </div>
              <div>
                <label className="text-xs tracking-[0.2em] uppercase font-bold text-[#5C5C5C] mb-2 block">Email</label>
                <Input data-testid="contact-email" type="email" placeholder="your@email.com" value={form.email}
                  onChange={e => setForm({...form, email: e.target.value})} required className="rounded-none border-[#E5E5E5] focus:ring-2 focus:ring-[#121212] h-11" />
              </div>
              <div>
                <label className="text-xs tracking-[0.2em] uppercase font-bold text-[#5C5C5C] mb-2 block">Phone</label>
                <Input data-testid="contact-phone" placeholder="+91 XXXX XXXX XX" value={form.phone}
                  onChange={e => setForm({...form, phone: e.target.value})} className="rounded-none border-[#E5E5E5] focus:ring-2 focus:ring-[#121212] h-11" />
              </div>
              <div>
                <label className="text-xs tracking-[0.2em] uppercase font-bold text-[#5C5C5C] mb-2 block">Message</label>
                <Textarea data-testid="contact-message" placeholder="Tell us about your project..." value={form.message}
                  onChange={e => setForm({...form, message: e.target.value})} required rows={4} className="rounded-none border-[#E5E5E5] focus:ring-2 focus:ring-[#121212] resize-none" />
              </div>
              <Button data-testid="contact-submit" type="submit" disabled={submitting}
                className="w-full bg-[#121212] text-white hover:bg-[#2A2A2A] rounded-none h-11 text-xs tracking-[0.15em] uppercase">
                {submitting ? 'Sending...' : 'Send Message'}
              </Button>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer data-testid="footer" className="border-t border-[#E5E5E5] py-12 bg-[#121212]">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid md:grid-cols-4 gap-8 mb-8">
          <div>
            <div className="flex items-center gap-3 mb-4">
              <img src={LOGO_URL} alt="S.A.I." className="h-10 w-auto" />
              <div>
                <span className="text-lg font-bold tracking-tight text-white">S.A.I.</span>
                <span className="text-xs tracking-[0.2em] uppercase block text-white/50 -mt-1">Design Group</span>
              </div>
            </div>
            <p className="text-sm text-white/50 leading-relaxed">Solution Added Ideas — Crafting spaces that inspire and endure.</p>
          </div>
          <div>
            <p className="text-xs tracking-[0.2em] uppercase font-bold text-white/70 mb-4">Quick Links</p>
            <div className="space-y-2">
              {["About", "Projects", "Services", "Contact"].map(item => (
                <a key={item} href={`#${item.toLowerCase()}`} className="block text-sm text-white/50 hover:text-white transition-colors">{item}</a>
              ))}
            </div>
          </div>
          <div>
            <p className="text-xs tracking-[0.2em] uppercase font-bold text-white/70 mb-4">Company</p>
            <div className="space-y-2">
              <a href="/careers" className="block text-sm text-white/50 hover:text-white transition-colors">Careers</a>
              <a href="/team" className="block text-sm text-white/50 hover:text-white transition-colors">Our Team</a>
            </div>
          </div>
          <div>
            <p className="text-xs tracking-[0.2em] uppercase font-bold text-white/70 mb-4">Follow Us</p>
            <div className="flex gap-3">
              <a href={SOCIAL.instagram} target="_blank" rel="noopener noreferrer" className="w-10 h-10 border border-white/20 flex items-center justify-center hover:border-white/50 transition-all"><InstagramIcon className="footer-icon" /></a>
              <a href={SOCIAL.facebook} target="_blank" rel="noopener noreferrer" className="w-10 h-10 border border-white/20 flex items-center justify-center hover:border-white/50 transition-all"><FacebookIcon className="footer-icon" /></a>
              <a href={SOCIAL.youtube} target="_blank" rel="noopener noreferrer" className="w-10 h-10 border border-white/20 flex items-center justify-center hover:border-white/50 transition-all"><YoutubeIcon className="footer-icon" /></a>
            </div>
          </div>
        </div>
        <Separator className="bg-white/10 mb-6" />
        <p className="text-xs text-white/30 text-center tracking-wider">&copy; {new Date().getFullYear()} S.A.I. Design Group. All rights reserved.</p>
      </div>
    </footer>
  );
}

// ====== SLIDING HONORS/PROJECTS ======
function HonorsSlider() {
  const [honors, setHonors] = useState([]);
  useEffect(() => {
    axios.get(`${API}/honors`).then(r => setHonors(r.data)).catch(() => {});
  }, []);

  // Use scenic images as defaults when no honors uploaded
  const defaultSlides = [
    { title: "Hamburg Residence", img: SCENIC.hero },
    { title: "Garden Design", img: SCENIC.garden },
    { title: "Night Ambiance", img: SCENIC.nightAerial },
    { title: "Glass House", img: SCENIC.rainyEvening },
    { title: "Sunlit Aerial", img: SCENIC.dayAerial },
    { title: "Interior Living", img: PROJECT_IMAGES.interiorLiving },
    { title: "Modern Facade", img: PROJECT_IMAGES.building },
  ];

  const slides = honors.length > 0
    ? honors.map(h => ({ title: h.title, img: `${API}/honors/${h.honor_id}/image` }))
    : defaultSlides;
  const allSlides = [...slides, ...slides];

  return (
    <section data-testid="honors-slider" className="py-12 border-t border-[#E5E5E5] bg-[#FDFCFB] overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 mb-8">
        <p className="text-xs tracking-[0.3em] uppercase text-[#5C5C5C] mb-4 font-bold">Highlights</p>
        <h2 className="text-3xl md:text-4xl tracking-tight leading-tight font-bold text-[#121212]">Projects & Honors</h2>
      </div>
      <div className="relative">
        <div className="absolute left-0 top-0 bottom-0 w-16 bg-gradient-to-r from-[#FDFCFB] to-transparent z-10" />
        <div className="absolute right-0 top-0 bottom-0 w-16 bg-gradient-to-l from-[#FDFCFB] to-transparent z-10" />
        <div className="flex gap-4 animate-honors-slide">
          {allSlides.map((s, i) => (
            <div key={i} className="flex-shrink-0 w-72 group">
              <div className="border border-[#E5E5E5] overflow-hidden bg-white">
                <div className="h-48 overflow-hidden">
                  <img src={s.img} alt={s.title} loading="lazy" decoding="async"
                    className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-700 group-hover:scale-105"
                    style={{ filter: undefined }}
                    onMouseEnter={e => e.target.style.filter = 'brightness(1.15) saturate(1.2)'}
                    onMouseLeave={e => e.target.style.filter = 'grayscale(100%)'}
                  />
                </div>
                {s.title && <p className="text-xs font-bold text-[#121212] p-3 border-t border-[#E5E5E5]">{s.title}</p>}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function LandingPage() {
  return (
    <div className="min-h-screen bg-[#FDFCFB]">
      <Navbar />
      <ScrollRevealHero />
      <ParallaxShowcase />
      <ContentIntro />
      <AboutSection />
      <ProjectsSection />
      <ServicesSection />
      <ClientLogosSection />
      <HonorsSlider />
      <CareersTeaser />
      <ContactSection />
      <Footer />
    </div>
  );
}
